import React, { useEffect, useRef, useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
} from "@mui/material";
import { Dialog } from "@material-ui/core";
import { ForkInfo } from "./ForkInfo";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { capitalizeFirstLetter } from "../../../utilities";
import { fetchKioskBoard } from "../fleetApi";

// Opened from a QR code on the machine, on a phone, with no app header — big
// touch targets and plain, high-contrast surfaces.

// The dialogs here come from @material-ui/core (v4), which sits outside the
// v5 MuiThemeBridge, so their Paper reads the app's floating-surface tokens
// directly (follows classic/glass x light/dark).
const dialogPaperProps = {
  style: {
    backgroundColor: "var(--app-popover-bg)",
    backgroundImage: "var(--app-glass-sheen)",
    color: "var(--app-text)",
    border: "1px solid var(--app-popover-border)",
    borderRadius: 24,
    boxShadow: "var(--app-popover-shadow)",
    WebkitBackdropFilter: "var(--app-glass-filter)",
    backdropFilter: "var(--app-glass-filter)",
  },
};

// options[1] is always the defect answer (see handleButtonClick).
const optionClass = (optionIndex) =>
  `app-btn app-btn-lg min-h-[3rem] flex-1 whitespace-normal border text-center ${
    optionIndex === 1
      ? "border-rose-500/40 bg-rose-500/10 text-rose-800 hover:bg-rose-500/20 dark:text-rose-200"
      : "border-sky-500/40 bg-sky-500/10 text-sky-800 hover:bg-sky-500/20 dark:text-sky-200"
  }`;

const ForkliftForm = () => {
  const [open, setOpen] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const [notsafeopen, setnotsafeOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedHelpItem, setSelectedHelpItem] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [selectedDefectItems, setSelectedDefectItems] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [image, setImage] = useState(null);
  const moffett = new URLSearchParams(window.location.search).get("m");
  const stepperRef = useRef(null);
  const { unit, branch } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Branches that run shifts (SBC): the kiosk passes ?shift=&sn=. A code on
  // the machine without one (or an old per-shift branch like sbc2) is
  // resolved here; if it's still unknown the operator picks their shift.
  const [shift, setShift] = useState({
    id: searchParams.get("shift") || "",
    name: searchParams.get("sn") || "",
  });
  const [shiftOptions, setShiftOptions] = useState([]);

  useEffect(() => {
    if (shift.id) return;
    let active = true;
    fetchKioskBoard(branch)
      .then((board) => {
        if (!active || !board.shifts?.length) return;
        const alias = board.shifts.find((s) => s.id === board.aliasShift);
        if (alias) setShift({ id: alias.id, name: alias.name });
        else setShiftOptions(board.shifts);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // Resolve once per branch; picking a shift sets shift.id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const needsShift = shiftOptions.length > 0 && !shift.id;
  const dashboardPath = `/fleet/forks/dashboard/${encodeURIComponent(branch)}${
    shift.id ? `?shift=${encodeURIComponent(shift.id)}` : ""
  }`;

  const markUnsafe = () => {
    const data = {
      unit: unit,
      branch: branch,
      ...selectedOptions,
      ...(shift.id ? { shift: shift.id } : {}),
    };

    fetch("/api/fleet/forklift/forkNotSafe", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },

      body: JSON.stringify(data),
    });
    navigate(dashboardPath);
  };

  useEffect(() => {
    const stepContainers = document.querySelectorAll(".MuiStep-root");

    if (stepContainers[activeStep]) {
      const viewportHeight = window.innerHeight;
      const stepContainerTop =
        stepContainers[activeStep].getBoundingClientRect().top;
      const scrollToPosition =
        stepContainerTop + window.scrollY - viewportHeight / 2;
      window.scrollTo({
        top: scrollToPosition,
        behavior: "smooth",
      });
    }
  }, [activeStep]);

  const handleImageUpload = () => {};

  const handleNameChange = (event) => {
    const inputValue = event.target.value;
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      name: inputValue,
    }));
  };

  const handleConfirmation = () => {
    setShowConfirmationDialog(true);
  };

  const handleNotSafe = (item) => {
    handleConfirmation();
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      notSafe: true,
      notSafeItem: item.title,
    }));
  };

  const handleButtonClick = (item, option) => {
    if (option === item.options[1]) {
      handleDefect(item.field);
    } else {
      setSelectedOptions((prevSelectedOptions) => ({
        ...prevSelectedOptions,
        [item.field]: option,
      }));

      setInputValues((prevInputValues) => {
        const updatedInputValues = { ...prevInputValues };
        updatedInputValues[item.field] = "";
        return updatedInputValues;
      });

      handleNext(); // Move to the next step after selecting an option

      if (selectedDefectItems.includes(item)) {
        setSelectedDefectItems((prevSelectedDefectItems) =>
          prevSelectedDefectItems.filter((defectItem) => defectItem !== item)
        );
      }
    }
  };

  const handleInputChange = (event, item) => {
    const inputValue = event.target.value;

    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [item.field]: inputValue,
    }));

    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      [`${item.field}`]: `defect_${inputValue}` || null,
    }));
  };

  const handleDefect = (field) => {
    const item = ForkInfo.find((info) => info.field === field);
    setSelectedItem(item);
    setOpen(true);
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      [`${item.field}`]: `defect_` || null,
    }));
    setSelectedDefectItems((prevSelectedDefectItems) => [
      ...prevSelectedDefectItems,
      item,
    ]);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenHelp(false);

    // Keep the defect buttons highlighted
    setSelectedDefectItems((prevSelectedDefectItems) =>
      prevSelectedDefectItems.filter(
        (defectItem) => inputValues[defectItem.field]
      )
    );

    // Proceed to the next step
    handleNext();
  };

  const handlelockoutClose = () => {
    setnotsafeOpen(false);
    markUnsafe();
  };

  const handleSubmit = async () => {
    const requiredFieldsCount = moffett === "true" ? 24 : 21; // Number of required fields based on moffett value
    const selectedFieldsCount = Object.keys(selectedOptions).length;

    if (selectedFieldsCount < requiredFieldsCount) {
      const missingFieldsCount = requiredFieldsCount - selectedFieldsCount;

      alert(
        `You have not filled in all the fields. You are missing ${missingFieldsCount} fields.`
      );

      return; // Do not proceed with submission
    }

    navigate(dashboardPath);

    const data = {
      unit: unit,
      branch: branch,
      ...selectedOptions,
      // Kept out of selectedOptions — the required-answer count above
      // counts its keys.
      ...(shift.id ? { shift: shift.id } : {}),
    };

    const rawResponse = await fetch("/api/fleet/forklift/submitEntry", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    const name = new URLSearchParams(window.location.search).get("name");

    if (name) {
      setSelectedOptions({
        ...selectedOptions,
        name: name,
      });
    }
  }, []);

  return (
    <div className="app-page min-h-screen pb-10">
      <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 px-4 pb-4 pt-6 text-center">
        <div>
          <p className="text-2xl font-bold tracking-tight">
            Unit #{unit} Checklist
          </p>
          <p className="app-muted text-lg font-semibold">
            {capitalizeFirstLetter(branch)}
            {shift.name ? ` · ${shift.name}` : ""}
          </p>
        </div>

        {needsShift && (
          <div className="w-full max-w-md">
            <p className="mb-2 font-semibold">Which shift are you on?</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {shiftOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setShift({ id: option.id, name: option.name })}
                  className="app-btn app-btn-lg app-btn-secondary w-full"
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <input
          type="text"
          placeholder="Enter Your Name"
          aria-label="Your name"
          autoComplete="name"
          value={selectedOptions.name}
          onChange={handleNameChange}
          className="app-input w-full max-w-md rounded-2xl border px-4 py-3 text-base"
        />
      </div>

      {!needsShift && selectedOptions.name && selectedOptions.name !== "" ? (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4">
          <div className="app-card rounded-2xl border p-3 sm:p-5">
            <Stepper
              ref={stepperRef}
              activeStep={activeStep}
              orientation="vertical"
              sx={{ background: "transparent" }}
            >
              {ForkInfo.map((item, index) => {
                const label = item.title;

                if (
                  (moffett === "true" &&
                    [
                      "Roll Over Cage",
                      "LP Gas Leaks (ODOR)",
                      "LP Cylinder Securely Mounted",
                    ].includes(label)) ||
                  (moffett === "false" &&
                    [
                      "Mounting Pins / Locks / Chains",
                      "Operators Manual",
                      "Hydraulic Control Operations",
                      "Air Filter Indicator",
                      "Parking Brake",
                      "Fire Extinguisher",
                    ].includes(label))
                ) {
                  return null; // Skip rendering this step
                }
                return (
                  <Step key={label}>
                    <StepLabel>
                      <span className="text-base font-semibold">
                        {label} [
                        {selectedOptions[item.field]?.replace(/defect_/g, "")}]
                      </span>
                    </StepLabel>
                    <div className="flex w-full">
                      <StepContent sx={{ flex: 1 }}>
                        <div className="flex flex-col gap-2 pt-1">
                          {/* Render the options for this step */}
                          <div className="flex w-full flex-wrap gap-2">
                            {item.options.map((option, optionIndex) => (
                              <button
                                type="button"
                                className={optionClass(optionIndex)}
                                key={optionIndex}
                                onClick={() => handleButtonClick(item, option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                          <div>
                            <Button
                              disabled={activeStep === 0}
                              onClick={handleBack}
                            >
                              Go Back
                            </Button>
                          </div>
                        </div>
                      </StepContent>
                    </div>
                  </Step>
                );
              })}
            </Stepper>
          </div>

          <label className="app-surface flex cursor-pointer flex-col items-center gap-2 rounded-2xl border border-dashed p-4 text-center">
            <span className="text-sm font-semibold">Upload an Image.</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full max-w-xs text-sm file:mr-3 file:rounded-full file:border-0 file:bg-sky-500/15 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-sky-700 dark:file:text-sky-300"
            />
            {image && (
              <img src={image} alt="Uploaded" className="mt-2 rounded-xl" />
            )}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="app-btn app-btn-lg app-btn-secondary w-full"
              onClick={handleBack}
            >
              Back
            </button>
            <button
              type="button"
              className="app-btn app-btn-lg app-btn-primary w-full"
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>

          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={"sm"}
            fullWidth={true}
            PaperProps={dialogPaperProps}
          >
            <div className="p-5 sm:p-6">
              <p className="border-b app-divider pb-3 text-lg font-semibold">
                {selectedItem?.description}
              </p>
              <input
                type="text"
                placeholder="Describe Defect"
                aria-label="Describe Defect"
                value={inputValues[selectedItem?.field] || ""}
                onChange={(event) => handleInputChange(event, selectedItem)}
                className="app-input mt-4 w-full rounded-xl border px-4 py-3 text-base"
              />
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    handleNotSafe(selectedItem);
                  }}
                  className="app-btn app-btn-lg app-btn-danger w-full"
                >
                  Not Safe
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="app-btn app-btn-lg app-btn-primary w-full"
                >
                  Save
                </button>
              </div>
            </div>
          </Dialog>
          <Dialog
            open={openHelp}
            onClose={handleClose}
            maxWidth={"sm"}
            fullWidth={true}
            PaperProps={dialogPaperProps}
          >
            <div className="p-5 sm:p-6">
              <p className="border-b app-divider pb-3 text-lg font-semibold">
                {selectedHelpItem?.title}
              </p>
              <p className="mt-3">{selectedHelpItem?.help}</p>
            </div>
          </Dialog>
          <Dialog
            open={showConfirmationDialog}
            onClose={() => setShowConfirmationDialog(false)}
            maxWidth={"sm"}
            fullWidth={true}
            PaperProps={dialogPaperProps}
          >
            <div className="p-5 text-center sm:p-6">
              <p className="text-lg font-semibold">
                Are you sure this vehicle is unsafe?
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setnotsafeOpen(true);
                  }}
                  className="app-btn app-btn-lg app-btn-danger w-full"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmationDialog(false);
                    setSelectedOptions((prevSelectedOptions) => ({
                      ...prevSelectedOptions,
                      notSafe: false,
                      notSafeItem: null,
                    }));
                  }}
                  className="app-btn app-btn-lg app-btn-secondary w-full"
                >
                  No
                </button>
              </div>
            </div>
          </Dialog>

          <Dialog
            open={notsafeopen}
            onClose={handlelockoutClose}
            maxWidth={"sm"}
            fullWidth={true}
            PaperProps={dialogPaperProps}
          >
            <div className="flex flex-col items-center gap-4 p-5 sm:p-6">
              {/* Display your photo here */}
              <img
                src={require("../../../assets/lockouttagout.jpg")}
                alt="Lockout / tagout"
                className="app-light-surface h-auto w-full rounded-xl bg-white"
              />

              {/* OK button to navigate back */}
              <button
                type="button"
                onClick={handlelockoutClose}
                className="app-btn app-btn-lg app-btn-primary w-full sm:w-auto sm:min-w-[10rem]"
              >
                OK
              </button>
            </div>
          </Dialog>
        </div>
      ) : (
        <p className="mx-auto mt-2 max-w-md rounded-2xl bg-amber-500/15 px-4 py-3 text-center font-bold text-amber-900 dark:text-amber-200">
          {needsShift ? "Pick your shift and enter your name to start" : "Enter Name to Start Checklist"}
        </p>
      )}
    </div>
  );
};

export default ForkliftForm;
